import type {ReactElement} from 'react';
import {useMemo, useState} from 'react';
import {Button, Size, Type} from '@Components/button';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import type {ChangeStyleTheme} from '@Components/ai-chat-window/ai-chat-window.types';
import type {ChangeStyleDropdownProps} from '@Components/ai-chat-window/components/change-style-dropdown/change-style-dropdown.types';
import {CHANGE_STYLE_DROPDOWN_MODAL_PANEL_ID, ChangeStyleSource} from '@Components/ai-chat-window/components/change-style-dropdown/change-style-dropdown.types';
import {getChangeStyleChainOriginalImage} from '@Components/ai-chat-window/lib/ai-chat-messages.library';
import {ChangeStyleMenuContent} from '@Components/ai-chat-window/components/change-style-dropdown/components/change-style-menu-content/change-style-menu-content';
import {ChangeStylePopup} from '@Components/ai-chat-window/components/change-style-dropdown/components/change-style-popup/change-style-popup';
import {useChangeStyleThemeSelect} from '@Components/ai-chat-window/hooks/useChangeStyleThemeSelect';
import {useUpdateMoreOptionsModal} from '@Components/modal/hooks/useUpdateMoreOptionsModal';
import {useAppSelector} from '@/hooks';
import styles from './change-style-dropdown.module.scss';

export function ChangeStyleDropdown(props: ChangeStyleDropdownProps): ReactElement {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState(ChangeStyleSource.ORIGINAL);
  const [selectedTheme, setSelectedTheme] = useState<ChangeStyleTheme | null>(null);

  const chatMessages = useAppSelector((state) => state.aiChatWindow[props.chatId]?.messages);
  const chainOriginalImage = useMemo(() => getChangeStyleChainOriginalImage(chatMessages, props.imageHashedId), [chatMessages, props.imageHashedId]);

  const restyleSourceImage =
    chainOriginalImage !== null && selectedSource === ChangeStyleSource.ORIGINAL
      ? {imageHashedId: chainOriginalImage.hashedId, imageWidth: chainOriginalImage.width ?? undefined, imageHeight: chainOriginalImage.height ?? undefined}
      : {imageHashedId: props.imageHashedId, imageWidth: props.imageWidth, imageHeight: props.imageHeight};

  const {onThemeSelect} = useChangeStyleThemeSelect({
    chatId: props.chatId,
    onGenerate: props.onGenerate,
    ...restyleSourceImage,
  });

  const closeAndCommitTheme = (theme: ChangeStyleTheme): void => {
    setIsOpen(false);
    onThemeSelect(theme);
  };

  const handleThemeTileClick = (theme: ChangeStyleTheme): void => {
    if (chainOriginalImage) {
      setSelectedTheme(theme);
      return;
    }
    closeAndCommitTheme(theme);
  };

  const handleApply = (): void => {
    if (selectedTheme === null) {
      return;
    }
    closeAndCommitTheme(selectedTheme);
  };

  const handleOpen = (): void => {
    setSelectedSource(ChangeStyleSource.ORIGINAL);
    setSelectedTheme(null);
    setIsOpen(true);
  };

  const handleClose = (): void => {
    setIsOpen(false);
  };

  const menuContent = (
    <ChangeStyleMenuContent
      onThemeTileClick={handleThemeTileClick}
      chainOriginalImage={chainOriginalImage}
      currentImageHashedId={props.imageHashedId}
      selectedSource={selectedSource}
      onSourceSelect={setSelectedSource}
      selectedTheme={selectedTheme}
      onApply={handleApply}
    />
  );

  useUpdateMoreOptionsModal(menuContent, [selectedSource, selectedTheme, chainOriginalImage], {panelIdOpenedInModal: CHANGE_STYLE_DROPDOWN_MODAL_PANEL_ID});

  const getChangeStyleButton = (): ReactElement => {
    return (
      <Button
        customClasses={`radius-8 _full-height ${styles.changeStyleButton}`}
        type={Type.SECONDARY}
        size={Size.XSMALL}
        text={window.i18next.t('pmwjs_change_style')}
        icon={'icon-repeat'}
        disabled={!props.canWrite}
        isActive={isOpen}
      />
    );
  };

  return (
    <Dropdown
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        isResumable
        isSmallScreenModalBottomSheet={false}
        doCloseOnInnerClick={false}
        showModalForSmallScreen
        modalTitle={window.i18next.t('pmwjs_ai_chat_change_style_heading')}
        modalContent={menuContent}
        modalClassName={styles.modalContent}
        panelIdForModal={CHANGE_STYLE_DROPDOWN_MODAL_PANEL_ID}
        alignment={ALIGNMENT_TYPE.RIGHT_END}
        popupClassName={styles.popup}
        className={styles.positionUnset}
        selector={getChangeStyleButton()}
        popup={<ChangeStylePopup>{menuContent}</ChangeStylePopup>}
        popupOffset={8}
    />
  );
}
